/**
 * Product page constants for customization, accordions, and support features.
 * Used by product detail page components.
 *
 * Types are defined inline — this file is self-contained.
 */

// ─── Customization ─────────────────────────────────────────────────────────

/** Prices added when a customer selects customization options */
export const CUSTOMIZATION_PRICING = {
  nameAndNumber: 14.95,
  sleeveBadge: 6,
}

/** UI labels and placeholder text for customization inputs */
export const CUSTOMIZATION_LABELS = {
  sectionTitle: "Personalise your kit",
  sectionOptional: "(optional)",
  sectionDescription:
    "Personalise your item or create the perfect gift.",
  nameAndNumberTitle: "Add Name and Number",
  customNamePlaceholder: "Name",
  customNumberPlaceholder: "Number",
  customNameHint: "12 max chars",
  customNumberHint: "2 max digits",
}

/** Max character limits for customization inputs */
export const CUSTOMIZATION_LIMITS = {
  nameMaxLength: 12,
  numberMaxLength: 2,
}

/** Patch options for sleeve badge selection */
export const CUSTOMIZATION_PATCHES = [
  {
    id: "none",
    name: "None",
    price: 0,
    image: "",
  },
  {
    id: "premiership",
    name: "Premiership",
    price: 6,
    image: "/images/Premier League.png",
  },
]

// ─── Product Accordions ─────────────────────────────────────────────────────

export type AccordionEmphasis = "bold" | "italic"

/** A run of text with optional inline emphasis — renders as plain text, <strong>, or <em> */
export interface AccordionTextSegment {
  text: string
  emphasis?: AccordionEmphasis
}

export interface AccordionContentItem {
  /**
   * "term" groups consecutive items into a single definition list (<dt>/<dd> pairs) —
   * matches the reference site's Delivery Info layout.
   * "heading" renders `question` as a standalone heading above the paragraph —
   * matches the reference site's Return Information layout.
   * "list" renders `question` as a heading (when present) followed by a bullet
   * list built from `items` — matches the reference site's product spec rows
   * (Item Condition, Colour, Season, etc.) in the Product Description tab.
   * Omitted renders a plain paragraph with no heading.
   */
  type?: "term" | "heading" | "list"
  question?: string
  answer: AccordionTextSegment[]
  /** Used only when type is "list" — one entry per <li> */
  items?: AccordionTextSegment[][]
}

export interface AccordionSectionData {
  title: string
  content: AccordionContentItem[]
}

export const PRODUCT_ACCORDION_DELIVERY_INFO: AccordionContentItem[] = [
  {
    answer: [
      {
        text: "The majority of the items on our website are in stock and ready for immediate processing, however to allow us to offer the widest possible range of football merchandise, some additional lead times do apply to certain products as documented below.",
      },
    ],
  },
  {
    answer: [
      {
        text: "We process new orders up until 2pm each day, after which point your order is considered as being placed the following day. (In reality, we continue processing after 2pm, but this is our stated cut-off and we cannot guarantee same day processing for orders placed after this point. In a small % of circumstances where our card processors flag up your order as high risk, we may need to make additional checks on your payment card which could delay your order. This is to reduce the risk of fraud.)",
      },
    ],
  },
  {
    answer: [
      {
        text: "The following types of orders have the additional processing lead-times.",
      },
      {
        text: " Please note that in many cases, we dispatch faster than this, but would rather quote longer lead-times and deliver faster than you expect than vice versa.",
        emphasis: "italic",
      },
    ],
  },
  {
    type: "term",
    question: "Immediate Dispatch",
    answer: [
      {
        text: "On average, products marked for immediate dispatch, which do not include printing, are shipped the same business day if ordered before 2pm.",
      },
    ],
  },
  {
    type: "term",
    question: "Printed Shirts",
    answer: [
      { text: "On average these are shipped within " },
      { text: "2-5 business days", emphasis: "bold" },
      {
        text: ". Depending on order volumes, next day or even same day shipments are often possible, but at peak times, these can take around 7-10 business days. In very rare circumstances, please allow up to 28 days.",
      },
    ],
  },
  {
    type: "term",
    question: "Other Personalised Products",
    answer: [
      { text: "On average these are shipped within " },
      { text: "2-5 business days", emphasis: "bold" },
      {
        text: ". Depending on order volumes, next day or even same day shipments are often possible, but at peak times, these can take around 7-10 business days. In very rare circumstances, please allow up to 28 days.",
      },
    ],
  },
  {
    type: "term",
    question: "T-Shirts",
    answer: [
      {
        text: "On average these are shipped within 2-5 business days. Depending on order volumes, next day or even same day shipments are often possible, but at peak times, these can take around 7-10 business days.",
      },
    ],
  },
  {
    type: "term",
    question: "Toffs & Copa Products",
    answer: [
      { text: "On average, these are shipped within " },
      { text: "14 days", emphasis: "bold" },
      { text: " (unless marked as " },
      { text: "Immediate Dispatch", emphasis: "bold" },
      {
        text: " on the product page) but are often faster. However, please allow up to 4-6 weeks for delivery.",
      },
    ],
  },
  {
    type: "term",
    question: "Concept Shirts",
    answer: [
      { text: "On average, these are shipped within " },
      { text: "10-14 days", emphasis: "bold" },
      { text: " (unless marked as " },
      { text: "Immediate Dispatch", emphasis: "bold" },
      {
        text: " on the product page) but are often faster. However, please allow up to 28 days for delivery.",
      },
    ],
  },
  {
    type: "term",
    question: "Non-Printed Products with Additional Lead Time",
    answer: [
      {
        text: "Due to the high range of merchandise we sell, on occasion stock must be sourced from our partners. In such cases, please allow an additional 3-10 working days to complete your order. Having the ability to draw stock from multiple warehouses gives our customers access to the widest ranges of soccer merchandise worldwide. These products will not be marked with ",
      },
      { text: "Immediate Dispatch", emphasis: "bold" },
      { text: " on the product page." },
    ],
  },
]

export const PRODUCT_ACCORDION_RETURNS: AccordionContentItem[] = [
  {
    type: "heading",
    question: "Returns Policy",
    answer: [
      {
        text: "UK Soccer Shop are happy to accept the return of all products, as long as they remain in the original condition (including original tags and packaging). Please note this does not apply to shirts which have shirt printing, sleeve patches or our range of retro products.",
      },
    ],
  },
]

export const PRODUCT_ACCORDION_SECTIONS: Record<string, AccordionSectionData> =
  {
    description: {
      title: "Product Description",
      content: [],
    },
    deliveryInfo: {
      title: "Delivery Info",
      content: PRODUCT_ACCORDION_DELIVERY_INFO,
    },
    returns: {
      title: "Return Information",
      content: PRODUCT_ACCORDION_RETURNS,
    },
    reviews: {
      title: "Product Reviews",
      content: [],
    },
    qa: {
      title: "Questions And Answers",
      content: [],
    },
  }

// ─── Product Support Features ───────────────────────────────────────────────

export interface ProductSupportFeature {
  id: string
  title: string
  description: string
  image: string
}

export const PRODUCT_SUPPORT_FEATURES: ProductSupportFeature[] = [
  {
    id: "fan-support",
    title: "Fan-first support",
    description:
      "Our team of football fans are here to help. We know our kits inside and out.",
    image: "/images/fan-support.jpg",
  },
  {
    id: "personalisation",
    title: "Expert personalisation",
    description:
      "Professional name and number printing done right. Official fonts for all major leagues.",
    image: "/images/personalisation.jpg",
  },
  {
    id: "fast-delivery",
    title: "Fast worldwide delivery",
    description:
      "Next day UK delivery available. We ship to over 100 countries worldwide.",
    image: "/images/fast-delivery.jpg",
  },
]

// ─── Feature Highlights ─────────────────────────────────────────────────────

export const PRODUCT_FEATURE_HIGHLIGHTS = [
  "Add any name and number",
  "Official, authentic kit, shipped from the UK",
  "Over 2 million orders shipped since 2004",
]

// ─── Pay Later Providers ────────────────────────────────────────────────────

export const PAY_LATER_INSTALLMENTS = 3

// ─── Shipping Method Preview ────────────────────────────────────────────────
// Countries covered by a configured region + fulfillment service zone.
// Keep in sync with admin-medusa's /store/shipping-preview supported list.

export const SHIPPING_PREVIEW_COUNTRIES: { code: string; name: string }[] = [
  { code: "gb", name: "United Kingdom" },
  { code: "de", name: "Germany" },
  { code: "dk", name: "Denmark" },
  { code: "es", name: "Spain" },
  { code: "fr", name: "France" },
  { code: "it", name: "Italy" },
  { code: "se", name: "Sweden" },
]

// ─── Size Guide ──────────────────────────────────────────────────────────────

export const SIZE_GUIDE_CHART: { size: string; chest: string }[] = [
  { size: "Small", chest: '34-36" (88/96cm)' },
  { size: "Medium", chest: '38-40" (96-104cm)' },
  { size: "Large", chest: '42-44" (104-112cm)' },
  { size: "XL", chest: '46-48" (112-124cm)' },
  { size: "XXL", chest: '50-52" (124/136cm)' },
  { size: "XXXL", chest: '54-56" (136-148cm)' },
]

export const SIZE_GUIDE_MEASUREMENTS = [
  {
    label: "Chest",
    instructions:
      "Run a flexible tape measure across the fullest area of your chest, holding the tape measure horizontally.",
  },
  {
    label: "Waist",
    instructions:
      "Measure around the narrowest part of the waist, keeping the tape horizontally.",
  },
  {
    label: "Hip",
    instructions:
      "Stand with your feet together and measure around the fullest point of the hip, keeping the tape measure horizontally.",
  },
]

export const SIZE_GUIDE_FIT_ADVICE =
  "If you're between two sizes, order based on your chest measurement for the best fit. Prefer a snug fit? Choose the smaller size. Prefer a relaxed fit? Choose the larger size."
